import type { FormFieldConfig } from '@/components/ContactForm.tsx';
import { ContactForm } from '@/components/ContactForm.tsx';
import { GradientText } from '@/components/GradientText.tsx';
import { Layout } from '@/components/Layout.tsx';
import { PageHero } from '@/components/PageHero.tsx';
import {
  Divider,
  Eyebrow,
  FeatureCard,
  Icons,
  SectionIntro,
} from '@/components/ui.tsx';
import { AUDIENCE_OPTIONS, REFERRAL_OPTIONS } from '@/data/content.ts';
import { SITE, WEBHOOKS } from '@/data/site.ts';

const INSIDE = [
  {
    icon: Icons.video(),
    title: 'Video Walkthroughs',
    body: 'Product overviews and strategy explainers walking through how our tax equity programs work from investment to return.',
  },
  {
    icon: Icons.document(),
    title: 'Case Studies',
    body: 'Real client scenarios showing offset structures, timelines, and outcomes across a range of tax liability sizes.',
  },
  {
    icon: Icons.checkSquare(),
    title: 'Tax Materials',
    body: "General tax opinion letters, ITC documentation, and reference materials to support your own or your client's review.",
  },
  {
    icon: Icons.chart(),
    title: 'Strategy Overviews',
    body: 'A closer look at how we structure investments, evaluate opportunities, and manage risk across our energy programs.',
  },
] as const;

// Field ids are prefixed with "dr-" so they never collide with the home-page
// contact form if both ever render on one document.
const FIELDS: readonly FormFieldConfig[] = [
  { name: 'name', label: 'Full Name', type: 'text', required: true },
  { name: 'email', label: 'Email Address', type: 'email', required: true },
  { name: 'firm', label: 'Firm / Company', type: 'text' },
  {
    name: 'interest',
    label: 'I Am A',
    type: 'select',
    options: AUDIENCE_OPTIONS,
  },
  {
    name: 'referral',
    label: 'How Did You Hear About Us?',
    type: 'select',
    options: REFERRAL_OPTIONS,
  },
  {
    name: 'message',
    label: 'What Are You Hoping to Review?',
    type: 'textarea',
    placeholder: 'Case studies, tax materials, strategy overview, etc.',
  },
];

export function DataRoom() {
  return (
    <Layout currentPath="/data-room">
      <PageHero
        image="images/hero-data-room.webp"
        imageAlt=""
        width={1920}
        height={1280}
        eyebrow="Due Diligence"
        heading={
          <>
            The Lux{' '}
            <GradientText as="em" immediate startDelay={0.5}>
              Data Room
            </GradientText>
          </>
        }
        lead="Case studies, strategy overviews, tax materials, and video walkthroughs — everything an advisor or client needs to evaluate Lux Financial, in one secure place."
      />

      <section className="section-pad">
        <div className="container">
          <SectionIntro>
            <h2>
              <GradientText as="em" startDelay={0.2}>
                What&rsquo;s Inside
              </GradientText>
            </h2>
            <p className="lead">
              A curated library built for advisors, CPAs, family offices, and
              clients doing their own due diligence on tax equity investment
              through the Investment Tax Credit.
            </p>
          </SectionIntro>
          {/* Original grid--4: 1 col <768px, 2 cols <1024px, 4 above. */}
          <div className="grid grid-cols-1 gap-[clamp(1.5rem,3vw,3rem)] md:grid-cols-2 lg:grid-cols-4">
            {INSIDE.map((item, i) => (
              <FeatureCard
                key={item.title}
                icon={item.icon}
                title={item.title}
                {...(i > 0 ? { delayClass: `fade-in-delay-${i}` } : {})}
              >
                {item.body}
              </FeatureCard>
            ))}
          </div>
        </div>
      </section>

      <section
        className="cta-section contact-section section-pad"
        id="request-access"
      >
        <div className="container">
          <div className="contact-grid">
            <div className="fade-in">
              <Eyebrow>Request Access</Eyebrow>
              <h2>
                See What We&rsquo;re{' '}
                <GradientText as="em" startDelay={0.2}>
                  Built On
                </GradientText>
              </h2>
              <p className="lead">
                Tell us a bit about yourself and we&rsquo;ll send over secure
                access to the data room, typically within one business day.
              </p>
              <Divider />
              <div className="contact-info__item">
                <h4>Phone</h4>
                <p>
                  <a href={SITE.phoneHref}>{SITE.phone}</a>
                </p>
              </div>
              <div className="contact-info__item">
                <h4>Headquarters</h4>
                <p>
                  <a
                    href={SITE.mapsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {SITE.address.street}
                    <br />
                    {SITE.address.locality}, {SITE.address.region}{' '}
                    {SITE.address.postalCode}
                  </a>
                </p>
              </div>
            </div>
            <div className="contact-form fade-in fade-in-delay-1">
              <ContactForm
                action={WEBHOOKS.dataRoom}
                turnstileAction="data-room"
                fields={FIELDS}
                idPrefix="dr-"
                submitLabel="Request Data Room Access"
                successTitle="Thank you — your request is in."
                successBody="We'll review your details and send secure data room access, typically within one business day."
              />
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}
